import { useEffect, useMemo, useState } from 'react'
import { Modal } from 'components'
import ErrorMessage from 'components/forms/error-message'
import { lang } from 'config'
import _ from 'lodash'
import { Plus } from 'components/icons/outline'
import axios from 'axios'
import { toast } from 'react-hot-toast'
import nProgress from 'nprogress'
import Label from 'components/forms/label'
import useSWR from 'swr'
import SelectSearchable from 'components/forms/select-searchable'

const fetcher = async (url, params) => {
  return await axios.get(url, { params }).then((res) => res.data.data)
}

const Content = ({ data, onSubmit }) => {
  const baseUrl = `${process.env.REACT_APP_INVENTORY_API_URL}/product/${data?.level}`

  // ✅ server search state
  const [search, setSearch] = useState('')
  const [searchValue, setSearchValue] = useState('') // input value (instant)
  const [page, setPage] = useState(1)
  const perPage = 20

  const orderBy =
    data?.level === 'brand'
      ? 'brand.name'
      : data?.level === 'series'
      ? 'series.name'
      : 'name'

  // ✅ debounce only updates "search" (which triggers SWR)
  const debouncedSetSearch = useMemo(
    () =>
      _.debounce((val) => {
        setPage(1) // reset page on new search
        setSearch(val)
      }, 400),
    []
  )

  useEffect(() => {
    return () => debouncedSetSearch.cancel()
  }, [debouncedSetSearch])

  const onSearchChange = (val) => {
    setSearchValue(val)
    debouncedSetSearch(val)
  }

  // ✅ list fetch is paginated + searchable
  const listParams = useMemo(
    () => ({
      paginate: true,
      page,
      per_page: perPage,
      search, // <— IMPORTANT
      order_by: orderBy,
      direction: 'asc',
    }),
    [page, perPage, search, orderBy]
  )

  const { data: targetSelection, isLoading: isLoadingTargetSelection } = useSWR(
    data?.level ? [baseUrl, listParams] : null,
    fetcher,
    {
      revalidateOnFocus: false,
    }
  )

  const [targetId, setTargetId] = useState()
  const [errors, setErrors] = useState({})

  // ✅ fetch selected object by id (stable even when list is paginated)
  const { data: selectedTarget, isLoading: isLoadingSelectedTarget } = useSWR(
    targetId && data?.level ? [`${baseUrl}/${targetId}`, {}] : null,
    fetcher,
    { revalidateOnFocus: false }
  )

  const target = selectedTarget

  const handleSubmit = (e) => {
    e.preventDefault()

    if (!targetId) {
      toast.error('Please select target')
      return
    }

    if (!target) {
      toast.error('Target is still loading')
      return
    }

    if (!nProgress.isStarted()) {
      nProgress.start()
      nProgress.done()
      onSubmit(target)
    } else {
      toast.error('Previous action is loading')
    }
  }

  return (
    <div className="mt-8 space-y-8">
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <Label htmlFor="target" value="Target" />

            <SelectSearchable
              disabled={!data?.level}
              selection={targetSelection?.data ?? []}
              isLoading={isLoadingTargetSelection || isLoadingSelectedTarget}
              value={targetId}
              onChange={(value) => setTargetId(value)}
              keyValue={(value) => value.id}
              title={(value) =>
                data?.level === 'brand'
                  ? value.brand?.name
                  : data?.level === 'series'
                  ? value.series?.name
                  : value.name
              }
              description={(value) =>
                data?.level === 'brand'
                  ? value.product_type?.name
                  : data?.level === 'series'
                  ? value.product_brand?.brand.name
                  : value.long_name
              }
              placeholder={`${data?.level ? 'Select Target' : 'Select Level First'}`}
              error={errors.target}
              reverse

              // ✅ YOU NEED THIS IN YOUR SelectSearchable:
              // a controlled search input that calls this handler
              serverSearch
              searchValue={searchValue}
              onSearchChange={onSearchChange}

              // ✅ optional paging controls (if your component supports it)
              page={page}
              onNextPage={() => setPage((p) => p + 1)}
              onPrevPage={() => setPage((p) => Math.max(1, p - 1))}
            />

            <ErrorMessage error={errors.target} />
          </div>
        </div>
      </div>

      <div className="text-xs">
        <button
          onClick={handleSubmit}
          className="items-center px-6 py-3 text-white transition bg-neutral-800 rounded-xl active:hover:scale-90"
        >
          <span>{lang.save}</span>
        </button>
      </div>
    </div>
  )
}

const Add = ({ data, onSubmit }) => {
  const [isOpen, setIsOpen] = useState(false)

  const openModal = () => setIsOpen(true)
  const closeModal = () => setIsOpen(false)

  const handleSubmit = (payload) => {
    onSubmit(payload)
    closeModal()
  }

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        className="inline-flex items-center px-4 py-3 space-x-2 transition border border-neutral-200 bg-neutral-50 rounded-xl active:hover:scale-90"
      >
        <Plus className="w-4 h-4" stroke={1.5} />
        <span>{lang.create}</span>
      </button>

      <Modal
        isOpen={isOpen}
        onClose={closeModal}
        title={`${lang.create} ${lang.data}`}
        content={<Content data={data} onSubmit={handleSubmit} />}
      />
    </>
  )
}

export default Add